import { Component, ElementRef, EventEmitter, Input, Output, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { UserService } from '../user.service';
import { SpinnerService } from './../../../shared/services/spinner.service';
import { AssignUserSkillsRequestDto } from '../models/assign-user-skills-request-dto';
import { AssignedSkillResponseDto } from '../models/assigned-skill-response-dto';
import { UserProfileConstants } from '../user-profile-constants';
import { SkillSearchComponent } from '../../../skill-search/components/skill-search.component';

@Component({
  selector: 'app-assign-skills-slider',
  templateUrl: './assign-skills-slider.component.html',
  styleUrls: ['./assign-skills-slider.component.scss']
})
export class AssignSkillsSliderComponent implements OnChanges {
  @Input() isAssignSkillsSliderOpen = false;
  @Input() userPUID: string | null = null;
  @Output() assignSkillsSliderClosed = new EventEmitter<boolean>();
  @ViewChild(SkillSearchComponent) skillSearchComponent?: SkillSearchComponent;
  @ViewChild('assignSkillsFormWrap') assignSkillsFormWrap?: ElementRef<HTMLDivElement>;

  selectedSkills: any[] = [];
  selectedSkillsFromSearch: any[] = [];
  assignedSkills: AssignedSkillResponseDto[] = [];
  existingSkillCodes: Set<string> = new Set();
  isSaving = false;
  isLoadingAssignedSkills = false;
  hasSearched = false;
  hasSearchResults = false;
  isAssignSkillButtonEnabled = false;
  headerText = UserProfileConstants.Assign_Skills_Header_Text;

  // Dialog properties
  removeReasonDialogVisible = false;
  revocationDialogVisible = false;
  selectedSkillForRemoval: any = null;
  get isAssignSkillButtonDisabled(): boolean {
    return !this.isAssignSkillButtonEnabled;
  }

  get hasNewSkillsToSave(): boolean {
    return this.selectedSkills.some(skill => !this.existingSkillCodes.has(skill?.code || ''));
  }

  constructor(
    private userService: UserService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private spinnerService: SpinnerService,
    private router: Router
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isAssignSkillsSliderOpen'] && this.isAssignSkillsSliderOpen && this.userPUID) {
      this.loadAssignedSkills();
    }
  }

  private loadAssignedSkills(): void {
    if (!this.userPUID) {
      return;
    }

    this.isLoadingAssignedSkills = true;
    this.userService.getAssignedSkillsForUser(this.userPUID).subscribe({
      next: (skills: AssignedSkillResponseDto[]) => {
        this.assignedSkills = skills || [];
        // Convert assigned skills to selectedSkills format and merge
        const convertedSkills = this.assignedSkills.map(skill => ({
          code: skill.skillCode,
          name: skill.skillName
        }));
        this.selectedSkills = [...convertedSkills];
        // Track existing skill codes
        this.existingSkillCodes = new Set(this.assignedSkills.map(s => s.skillCode));
        this.isLoadingAssignedSkills = false;
      },
      error: (error) => {
        console.error('Error loading assigned skills:', error);
        this.isLoadingAssignedSkills = false;
        this.messageService.add({
          key: UserProfileConstants.MyEmployeesProfileToastKey,
          severity: UserProfileConstants.Severity_Err,
          summary: 'Failed to load assigned skills'
        });
      }
    });
  }



  onSelectedSkillsChange(skills: any[]): void {
    this.selectedSkillsFromSearch = Array.isArray(skills) ? skills : [];
    this.isAssignSkillButtonEnabled = this.selectedSkillsFromSearch.length > 0;
  }

  onSearchStateChange(state: { hasSearched: boolean; hasRecords: boolean } | null | undefined): void {
    this.hasSearched = state?.hasSearched ?? false;
    this.hasSearchResults = state?.hasRecords ?? false;
  }

  assignSelectedSkills(): void {
    if (!this.selectedSkillsFromSearch.length) {
      return;
    }

    const existingSkillKeys = new Set(this.selectedSkills.map(skill => skill?.code || ''));
    const duplicateSkills = this.selectedSkillsFromSearch.filter(skill => existingSkillKeys.has(skill?.code || ''));
    const newSkills = this.selectedSkillsFromSearch.filter(skill => !existingSkillKeys.has(skill?.code || ''));

    if (duplicateSkills.length > 0) {
      const duplicateCodes = Array.from(new Set(
        duplicateSkills
          .map(skill => this.getSkillCode(skill))
          .filter((code): code is string => Boolean(code))
      ));

      this.messageService.add({
        key: UserProfileConstants.MyEmployeesProfileToastKey,
        severity: UserProfileConstants.Severity_Err,
        summary: this.buildValidationMessage(
          UserProfileConstants.Assign_Skills_Already_In_Selection,
          duplicateCodes
        ),
        sticky: true
      });
    }

    if (newSkills.length > 0) {
      this.selectedSkills = [...this.selectedSkills, ...newSkills];
    }

    this.scrollToTopOfSlider();
  }

  removeSelectedSkill(skill: any): void {
    const skillKey = skill?.code || '';
    
    // Allow removal of newly added (non-existing) skills directly
    if (!this.existingSkillCodes.has(skillKey)) {
      this.selectedSkills = this.selectedSkills.filter(item => (item?.code || '') !== skillKey);
      return;
    }

    // For existing skills: show appropriate dialog based on isManagerAssigned flag
    this.selectedSkillForRemoval = skill;
    const assignedSkill = this.assignedSkills.find(s => s.skillCode === skill.code);

    if (assignedSkill?.isManagerAssigned) {
      // Flow A: Manager-assigned skill → simple removal reason dialog
      this.removeReasonDialogVisible = true;
    } else {
      // Flow B: Auto-assigned skill → training items revocation dialog
      this.revocationDialogVisible = true;
    }
  }

  saveAssignedSkills(): void {
    if (!this.userPUID) {
      this.messageService.add({
        key: UserProfileConstants.MyEmployeesProfileToastKey,
        severity: UserProfileConstants.Severity_Err,
        summary: UserProfileConstants.Assign_Skills_User_Required
      });
      return;
    }

    // Only send newly selected skills (not existing ones)
    const newSkills = this.selectedSkills.filter(skill => !this.existingSkillCodes.has(skill?.code || ''));

    if (newSkills.length === 0) {
      this.messageService.add({
        key: UserProfileConstants.MyEmployeesProfileToastKey,
        severity: UserProfileConstants.Severity_Warning,
        summary: UserProfileConstants.Assign_Skills_No_Skills_Selected
      });
      return;
    }

    const skillCodes = Array.from(new Set(
      newSkills
        .map(skill => this.getSkillCode(skill))
        .filter((code): code is string => Boolean(code))
    ));

    const request: AssignUserSkillsRequestDto = {
      userPUID: this.userPUID,
      skillCodes
    };

    this.isSaving = true;
    this.userService.assignSkillsToUser(request).subscribe({
      next: (response: any) => {
        const messages = this.getResponseMessages(response);
        messages.forEach((message: { severity: string; summary: string }) => {
          this.messageService.add({
            key: UserProfileConstants.MyEmployeesProfileToastKey,
            severity: message.severity,
            summary: message.summary,
            sticky: message.severity === UserProfileConstants.Severity_Err
          });
        });
        this.isSaving = false;
        this.resetAndCloseSlider();
      },
      error: () => {
        this.messageService.add({
          key: UserProfileConstants.MyEmployeesProfileToastKey,
          severity: UserProfileConstants.Severity_Err,
          summary: UserProfileConstants.Assign_Skills_Save_Failed,
          sticky: true
        });
        this.isSaving = false;
        this.resetAndCloseSlider();
      },
      complete: () => {
        this.isSaving = false;
      }
    });
  }
  closeSlider(): void {
    this.confirmClose();
  }

  // Dialog callbacks
  onRemovalReasonConfirm(reason: string): void {
    this.unassignSkillAsManager(this.selectedSkillForRemoval, reason);
  }

  onRemovalReasonCancel(): void {
    this.removeReasonDialogVisible = false;
    this.selectedSkillForRemoval = null;
  }

  onRevocationConfirm(data: { itemIds: string[]; reason: string; nonCompleteItems: any[] }): void {

    this.revokeTrainingOccurrences(this.selectedSkillForRemoval, data);
  }

  onRevocationCancel(): void {
    this.revocationDialogVisible = false;
    this.selectedSkillForRemoval = null;
  }

  // Flow A: Unassign skill as manager
  private unassignSkillAsManager(skill: any, reason: string): void {
    const assignedSkill = this.assignedSkills.find(s => s.skillCode === skill.code);

    if (!assignedSkill) {
      this.messageService.add({
        key: UserProfileConstants.MyEmployeesProfileToastKey,
        severity: UserProfileConstants.Severity_Err,
        summary: 'Skill not found'
      });
      return;
    }

    this.isSaving = true;
    this.removeReasonDialogVisible = false;

    this.userService.unassignSkillAsManager(assignedSkill.userAssignedSkillId, reason).subscribe({
      next: () => {
        this.messageService.add({
          key: UserProfileConstants.MyEmployeesProfileToastKey,
          severity: UserProfileConstants.Severity_Success,
          summary: 'Skill removed successfully'
        });
        this.removeSkillFromList(skill);
        // Show spinner without message during backend processing
        this.spinnerService.forceShow();
        setTimeout(() => {
          this.spinnerService.hide();
          this.resetAndCloseSlider();
        }, 4000);
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error unassigning skill:', error);
        this.messageService.add({
          key: UserProfileConstants.MyEmployeesProfileToastKey,
          severity: UserProfileConstants.Severity_Err,
          summary: error?.error?.message || 'Failed to remove skill. Please try again.'
        });
        this.isSaving = false;
      }
    });
  }

  // Flow B: Revoke training occurrences for auto-assigned skill
  private revokeTrainingOccurrences(skill: any, data: { itemIds: string[]; reason: string; nonCompleteItems: any[] }): void {
    if (!this.userPUID) {
      this.messageService.add({
        key: UserProfileConstants.MyEmployeesProfileToastKey,
        severity: UserProfileConstants.Severity_Err,
        summary: 'User not found'
      });
      return;
    }

    const assignedSkill = this.assignedSkills.find(s => s.skillCode === skill.code);

    if (!assignedSkill) {
      this.messageService.add({
        key: UserProfileConstants.MyEmployeesProfileToastKey,
        severity: UserProfileConstants.Severity_Err,
        summary: 'Skill not found'
      });
      return;
    }

    this.isSaving = true;
    this.revocationDialogVisible = false;

    const revocationRequest = {
      userAssignedSkillId: assignedSkill.userAssignedSkillId,
      trainingItemIds: data.itemIds,
      revocationReason: data.reason
    };

    this.userService.revokeTrainingOccurrences(this.userPUID, revocationRequest).subscribe({
      next: (response: any) => {
        // Check if there are non-complete items from backend response
        if (response?.nonCompleteTrainingItemIds && response.nonCompleteTrainingItemIds.length > 0) {
          const itemCodes = response.nonCompleteTrainingItemIds.join(', ');
          this.messageService.add({
            key: UserProfileConstants.MyEmployeesProfileToastKey,
            severity: UserProfileConstants.Severity_Warning,
            summary: 'Training Requirements Not Updated',
            detail: `The following Training Requirements have not been updated because they are not in a Complete Training Status and cannot be revoked: ${itemCodes}`,
            sticky: true
          });
          this.isSaving = false;
          return; // Don't proceed further if there are non-complete items
        }

        // Only show success and proceed if operation was successful
        if (response?.success !== false) {
          this.messageService.add({
            key: UserProfileConstants.MyEmployeesProfileToastKey,
            severity: UserProfileConstants.Severity_Success,
            summary: 'Training revocation successful',
            detail: 'The training items have been successfully revoked.'
          });
          this.removeSkillFromList(skill);
          // Show spinner without message during backend processing
          this.spinnerService.forceShow();
          setTimeout(() => {
            this.spinnerService.hide();
            this.resetAndCloseSlider();
          }, 8000);
        }
        
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error revoking training occurrences:', error);
        this.messageService.add({
          key: UserProfileConstants.MyEmployeesProfileToastKey,
          severity: UserProfileConstants.Severity_Err,
          summary: 'Failed to revoke training. Please try again.'
        });
        this.isSaving = false;
      }
    });
  }

  private removeSkillFromList(skill: any): void {
    this.selectedSkills = this.selectedSkills.filter(item => (item?.code || '') !== (skill?.code || ''));
    this.existingSkillCodes.delete(skill?.code || '');
  }

  private confirmClose(): void {
    this.confirmationService.confirm({
      message: UserProfileConstants.User_Profile__Confirmation_Text,
      header: UserProfileConstants.User_Profile_Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.resetAndCloseSlider();
      }
    });
  }

  viewSkillDetails(skillCode: string, skillName: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/eqms.skillDetails/' + skillCode])
    );
    window.open(url, '_blank');
  }

  private resetAndCloseSlider(): void {
    this.resetSlider();
    this.assignSkillsSliderClosed.emit(true);
  }
  private resetSlider(): void {
    this.selectedSkills = [];
    this.selectedSkillsFromSearch = [];
    this.assignedSkills = [];
    this.existingSkillCodes = new Set();
    this.isSaving = false;
    this.hasSearched = false;
    this.hasSearchResults = false;
    this.isAssignSkillButtonEnabled = false;
    this.skillSearchComponent?.clearCurrentSelection();
  }

  private getSkillCode(skill: any): string | null {
    return skill?.code || null;
  }

  private getResponseMessages(response: any): Array<{ severity: string; summary: string }> {
    const messages: Array<{ severity: string; summary: string }> = [];

    if (this.hasResponseItems(response?.existingSkills)) {
      messages.push({
        severity: UserProfileConstants.Severity_Err,
        summary: this.buildValidationMessage(
          UserProfileConstants.Assign_Skills_Already_Assigned,
          response.existingSkills
        )
      });
    }

    if (this.hasResponseItems(response?.inactiveSkills)) {
      messages.push({
        severity: UserProfileConstants.Severity_Err,
        summary: this.buildValidationMessage(
          UserProfileConstants.Assign_Skills_Inactive,
          response.inactiveSkills
        )
      });
    }

    if (this.hasResponseItems(response?.incompatbleSkills)) {
      messages.push({
        severity: UserProfileConstants.Severity_Err,
        summary: this.buildValidationMessage(
          UserProfileConstants.Assign_Skills_Scope_Mismatch,
          response.incompatbleSkills
        )
      });
    }

    if (this.hasResponseItems(response?.assignedSkills)) {
      messages.push({
        severity: UserProfileConstants.Severity_Success,
        summary: this.buildValidationMessage(
          UserProfileConstants.Assign_Skills_Saved_Successfully,
          response.assignedSkills
        )
      });
    }

    return messages;
  }

  private buildValidationMessage(prefix: string, skillCodes: any[]): string {
    const codes = Array.isArray(skillCodes)
      ? skillCodes.filter((code: any) => typeof code === 'string' && code.trim().length > 0)
      : [];

    return codes.length > 0 ? `${prefix} ${codes.join(', ')}` : prefix;
  }

  private hasResponseItems(value: any): boolean {
    return Array.isArray(value) && value.some((item: any) => typeof item === 'string' && item.trim().length > 0);
  }

  private scrollToTopOfSlider(): void {
    this.assignSkillsFormWrap?.nativeElement.scrollTo(0, 0);
  }
}
